<template>
  <div class="animated fadeIn support-user-detail">
    <div class="top-back-action">
      <b-button
        size="sm"
        variant="outline-secondary"
        class="back-pill-btn"
        @click="goBack"
      >
        <i class="fa fa-arrow-left mr-1"></i>
        Voltar
      </b-button>
    </div>

    <b-row>
      <b-col cols="12">
        <b-card>
          <template #header>
            <div
              class="d-flex justify-content-between align-items-center flex-wrap"
            >
              <div>
                <h5 class="m-0">
                  Acompanhamento do protocolo {{ ticket && ticket.protocol }}
                </h5>
                <small class="text-muted"
                  >Visualizacao completa da solicitacao</small
                >
              </div>
              <div class="d-flex align-items-center mt-2 mt-md-0">
                <b-badge
                  class="mr-2"
                  :variant="statusVariant(ticket && ticket.status)"
                >
                  {{ statusLabel(ticket && ticket.status) }}
                </b-badge>
              </div>
            </div>
          </template>
          <b-card-body>
            <div v-if="loading" class="text-center py-5">
              <b-spinner></b-spinner>
              <span class="ml-2">Carregando...</span>
            </div>

            <div v-else-if="ticket">
              <div class="mb-3 detail-grid">
                <div>
                  <strong>Empresa:</strong>
                  {{
                    ticket.company &&
                    (ticket.company.fantasyName || ticket.company.businessName)
                  }}
                </div>
                <div>
                  <strong>Solicitante:</strong>
                  {{ ticket.requester && ticket.requester.name }}
                  {{ ticket.requester && ticket.requester.surname }}
                </div>
                <div><strong>Modulo:</strong> {{ ticket.module }}</div>
                <div>
                  <strong>Criado em:</strong> {{ formatDate(ticket.createdAt) }}
                </div>
              </div>

              <h6 class="mb-3">Linha do tempo</h6>
              <div class="timeline-list support-timeline">
                <div
                  v-for="(message, index) in ticket.messages"
                  :key="message._id"
                  class="timeline-item"
                  :class="{
                    'timeline-customer': isCustomerMessage(message),
                    'timeline-support': !isCustomerMessage(message),
                  }"
                >
                  <div class="timeline-col timeline-left">
                    <div
                      v-if="isCustomerMessage(message)"
                      class="timeline-content"
                    >
                      <div class="timeline-meta">
                        <strong>{{ getTimelineTitle(message, index) }}</strong>
                        <span class="text-muted">{{
                          formatDate(message.createdAt)
                        }}</span>
                      </div>
                      <div class="timeline-text" v-if="message.text">
                        {{ getTimelineBody(message) }}
                      </div>
                      <div
                        v-if="message.files && message.files.length"
                        class="timeline-files"
                      >
                        <b-button
                          v-for="file in message.files"
                          :key="file._id"
                          size="sm"
                          variant="link"
                          class="pl-0"
                          @click="
                            downloadFile(ticket._id, message._id, file._id)
                          "
                        >
                          <i class="fa fa-paperclip"></i> {{ file.name }}
                        </b-button>
                      </div>
                    </div>
                  </div>

                  <div class="timeline-center">
                    <div
                      class="timeline-node"
                      :class="{
                        'timeline-node-support': !isCustomerMessage(message),
                      }"
                    ></div>
                    <div class="timeline-date">
                      {{ formatShortDate(message.createdAt) }}
                    </div>
                  </div>

                  <div class="timeline-col timeline-right">
                    <div
                      v-if="!isCustomerMessage(message)"
                      class="timeline-content"
                    >
                      <div class="timeline-meta">
                        <strong>{{ getTimelineTitle(message, index) }}</strong>
                        <span class="text-muted">{{
                          formatDate(message.createdAt)
                        }}</span>
                      </div>
                      <div class="timeline-text" v-if="message.text">
                        {{ getTimelineBody(message) }}
                      </div>
                      <div
                        v-if="message.files && message.files.length"
                        class="timeline-files"
                      >
                        <b-button
                          v-for="file in message.files"
                          :key="file._id"
                          size="sm"
                          variant="link"
                          class="pl-0"
                          @click="
                            downloadFile(ticket._id, message._id, file._id)
                          "
                        >
                          <i class="fa fa-paperclip"></i> {{ file.name }}
                        </b-button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <hr />

              <b-alert
                v-if="isTicketConcluded"
                show
                variant="secondary"
                class="mb-3"
              >
                Chamado concluído. Não é possível enviar novas mensagens.
              </b-alert>

              <b-form @submit.prevent="sendMessage">
                <b-form-group label="Nova mensagem">
                  <b-form-textarea
                    v-model="newMessageText"
                    rows="4"
                    placeholder="Escreva uma atualizacao ou responda ao suporte"
                    :disabled="isTicketConcluded"
                  />
                </b-form-group>
                <b-form-group label="Anexo (opcional)">
                  <b-form-file
                    v-model="newMessageFile"
                    browse-text="Selecionar"
                    placeholder="Imagem ou documento"
                    :disabled="isTicketConcluded"
                  />
                </b-form-group>
                <b-button
                  type="submit"
                  variant="success"
                  :disabled="sendingMessage || isTicketConcluded"
                >
                  <b-spinner v-if="sendingMessage" small class="mr-1" />
                  Enviar atualizacao
                </b-button>
              </b-form>
            </div>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import {
  addSupportMessage,
  getSupportTicket,
  supportFileDownloadUrl,
} from "../../api/support";

export default {
  name: "SupportUserTicketDetail",
  data() {
    return {
      loading: false,
      sendingMessage: false,
      ticket: null,
      newMessageText: "",
      newMessageFile: null,
    };
  },
  computed: {
    ...mapGetters(["profile"]),
    isTicketConcluded() {
      const status = this.ticket && this.ticket.status;
      return status === "resolved" || status === "closed";
    },
  },
  mounted() {
    if (this.profile && this.profile.role === "root") {
      this.$router.replace({ path: "/suporte" });
      return;
    }
    this.loadTicket();
  },
  methods: {
    showSuccessToast(message, title = "Sucesso") {
      this.$bvToast.toast(message, {
        title,
        toastClass: "custom-toast custom-toast-success",
        variant: "light",
        solid: true,
        autoHideDelay: 5000,
        noCloseButton: false,
      });
    },
    showErrorToast(message, title = "Erro") {
      this.$bvToast.toast(message, {
        title,
        toastClass: "custom-toast custom-toast-error",
        variant: "light",
        solid: true,
        autoHideDelay: 10000,
        noCloseButton: false,
      });
    },
    formatDate(value) {
      if (!value) return "-";
      return new Date(value).toLocaleString("pt-BR");
    },
    formatShortDate(value) {
      if (!value) return "-";
      return new Date(value).toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
      });
    },
    statusLabel(status) {
      const labels = {
        open: "Aberto",
        in_progress: "Em atendimento",
        waiting_customer: "Aguardando cliente",
        resolved: "Resolvido",
        closed: "Fechado",
      };
      return labels[status] || status;
    },
    statusVariant(status) {
      const variants = {
        open: "warning",
        in_progress: "primary",
        waiting_customer: "info",
        resolved: "success",
        closed: "secondary",
      };
      return variants[status] || "secondary";
    },
    isCustomerMessage(message) {
      return !message || message.authorRole !== "support";
    },
    getTimelineTitle(message, index) {
      if (index === 0) {
        return "Descricao";
      }
      const text = (message && message.text) || "";
      if (text.startsWith("[SOLICITACAO_AJUDA_ROOT]")) {
        return "Solicitacao de ajuda ao root";
      }
      if (text.startsWith("[SOLICITACAO_CLIENTE]")) {
        return "Solicitacao para o cliente";
      }
      if (text.startsWith("[FINALIZACAO]")) {
        return "Finalizacao do suporte";
      }
      return this.isCustomerMessage(message)
        ? "Sua mensagem"
        : "Resposta do suporte";
    },
    getTimelineBody(message) {
      const text = (message && message.text) || "";
      return text
        .replace(/^\[SOLICITACAO_AJUDA_ROOT\]\s*/u, "")
        .replace(/^\[SOLICITACAO_CLIENTE\]\s*/u, "")
        .replace(/^\[FINALIZACAO\]\s*/u, "");
    },
    async loadTicket() {
      this.loading = true;
      try {
        const response = await getSupportTicket(this.$route.params.id);
        this.ticket = response.data;
      } catch (error) {
        const message =
          (error && error.response && error.response.data) ||
          "Erro ao carregar protocolo";
        this.showErrorToast(message);
        this.$router.replace({ path: "/suporte" });
      } finally {
        this.loading = false;
      }
    },
    async sendMessage() {
      if (!this.ticket) return;
      if (this.isTicketConcluded) {
        this.showErrorToast(
          "Chamado concluído. Não é possível enviar novas mensagens."
        );
        return;
      }
      this.sendingMessage = true;
      try {
        let payload = { text: this.newMessageText || "" };
        if (this.newMessageFile) {
          payload = new FormData();
          payload.append("text", this.newMessageText || "");
          payload.append("file", this.newMessageFile);
        }

        const response = await addSupportMessage(this.ticket._id, payload);
        this.ticket = response.data;
        this.newMessageText = "";
        this.newMessageFile = null;
      } catch (error) {
        const message =
          (error && error.response && error.response.data) ||
          "Erro ao enviar atualizacao";
        this.showErrorToast(message);
      } finally {
        this.sendingMessage = false;
      }
    },
    downloadFile(ticketId, messageId, fileId) {
      window.open(
        supportFileDownloadUrl(ticketId, messageId, fileId),
        "_blank"
      );
    },
    goBack() {
      this.$router.push({ path: "/suporte" });
    },
  },
};
</script>

<style scoped>
.top-back-action {
  display: flex;
  justify-content: flex-start;
  margin-bottom: 0.75rem;
}

.back-pill-btn {
  border-radius: 30px;
  padding: 0.35rem 0.9rem;
  font-weight: 600;
  color: #81868b;
}

.back-pill-btn:hover,
.back-pill-btn:focus {
  color: #000;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.5rem 1rem;
}

.timeline-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.support-timeline {
  position: relative;
}

.support-timeline::before {
  content: "";
  position: absolute;
  left: 50%;
  top: 0;
  bottom: 0;
  width: 2px;
  background: #d6e3f9;
  transform: translateX(-50%);
}

.timeline-item {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 84px minmax(0, 1fr);
  column-gap: 0.75rem;
  align-items: start;
  z-index: 1;
}

.timeline-col {
  min-width: 0;
}

.timeline-left {
  display: flex;
  justify-content: flex-end;
}

.timeline-right {
  display: flex;
  justify-content: flex-start;
}

.timeline-content {
  width: 100%;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 0.85rem;
  background: #fff;
}

.timeline-customer .timeline-content {
  border-left: 3px solid #17a2b8;
}

.timeline-support .timeline-content {
  border-left: 3px solid #0d6efd;
}

.timeline-center {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 0.85rem;
  gap: 0.35rem;
}

.timeline-node {
  width: 14px;
  height: 14px;
  border: 2px solid #17a2b8;
  border-radius: 50%;
  background: #fff;
}

.timeline-node-support {
  border-color: #0d6efd;
  background: #0d6efd;
}

.timeline-date {
  font-size: 0.75rem;
  color: #6b7785;
  text-align: center;
}

.timeline-meta {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.35rem;
  gap: 0.5rem;
}

.timeline-text {
  white-space: pre-wrap;
  line-height: 1.45;
  color: #3f4a57;
}

@media (max-width: 992px) {
  .detail-grid {
    grid-template-columns: 1fr;
  }

  .support-timeline::before {
    left: 20px;
  }

  .timeline-item {
    grid-template-columns: 28px minmax(0, 1fr);
    column-gap: 0.6rem;
  }

  .timeline-left,
  .timeline-right {
    justify-content: flex-start;
    grid-column: 2;
  }

  .timeline-center {
    grid-column: 1;
    grid-row: 1;
    padding-top: 0.9rem;
  }

  .timeline-date {
    font-size: 0.7rem;
  }
}
</style>
